import { ReportQASectionEvaluation, ReportQASectionStatus } from "@api/reportQa";

import { cn } from "@utils/ui";

/**
 * Donut chart for the "Overall Report Quality" header card.
 *
 * Segments are sized by the count of section_evaluations per status
 * (Strong / Weak / Missing), so the ring at a glance shows how much
 * of the report falls into each band. The numeric overall_score sits
 * in the centre, coloured by the severity_overall label.
 *
 * Pure SVG so we don't pull in a chart library for one ring.
 */
type Props = {
  score: number;
  severityLabel?: string | null;
  evaluations: ReportQASectionEvaluation[];
  className?: string;
};

const RADIUS = 42;
const STROKE_WIDTH = 10;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// Order arcs Strong → Weak → Missing clockwise from 12 o'clock so the
// "good" segment always reads first. Missing intentionally maps to the
// background gray so the unfilled portion of the score visually equals
// the count of missing sections — the design only specs three colours.
const STATUS_ORDER: ReportQASectionStatus[] = ["strong", "weak", "missing"];

const STATUS_STROKE: Record<ReportQASectionStatus, string> = {
  strong: "stroke-[#2563EB]",
  weak: "stroke-[#F59E0B]",
  missing: "stroke-[#D1D5DB]",
};

// Severity_overall comes from the function-app prompt and uses the
// vocabulary "Excellent" | "Good" | "Fair" | "Poor" | "Critical". The
// section-status words are also accepted defensively in case the LLM
// drifts. Falls back to score bands when the label is absent.
export const severityToToneClass = (
  label: string | null | undefined,
  score: number
): string => {
  const normalised = label?.toLowerCase().trim();
  if (
    normalised === "excellent" ||
    normalised === "good" ||
    normalised === "strong"
  ) {
    return "text-success";
  }
  if (normalised === "fair" || normalised === "weak") {
    return "text-warning";
  }
  if (
    normalised === "poor" ||
    normalised === "critical" ||
    normalised === "missing"
  ) {
    return "text-destructive";
  }
  if (score >= 80) return "text-success";
  if (score >= 50) return "text-warning";
  return "text-destructive";
};

export const OverallScoreDonut = ({
  score,
  severityLabel,
  evaluations,
  className,
}: Props) => {
  const clamped = Math.max(0, Math.min(100, score));
  const counts = evaluations.reduce(
    (acc, e) => {
      acc[e.status] += 1;
      return acc;
    },
    { strong: 0, weak: 0, missing: 0 } as Record<ReportQASectionStatus, number>
  );
  const total = counts.strong + counts.weak + counts.missing;
  const textTone = severityToToneClass(severityLabel, clamped);

  let cursor = 0;
  const segments = STATUS_ORDER.filter((status) => counts[status] > 0).map(
    (status) => {
      const length = (counts[status] / total) * CIRCUMFERENCE;
      const dashOffset = -cursor;
      cursor += length;
      return { status, length, dashOffset, strokeClass: STATUS_STROKE[status] };
    }
  );

  return (
    <div className={cn("relative size-24 shrink-0", className)}>
      <svg viewBox="0 0 100 100" className="size-full -rotate-90" aria-hidden="true">
        <circle
          cx="50"
          cy="50"
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE_WIDTH}
          className="stroke-[#D1D5DB] dark:stroke-slate-700"
        />
        {total > 0
          ? segments.map((seg) => (
              <circle
                key={seg.status}
                cx="50"
                cy="50"
                r={RADIUS}
                fill="none"
                strokeWidth={STROKE_WIDTH}
                strokeLinecap="butt"
                strokeDasharray={`${seg.length} ${CIRCUMFERENCE - seg.length}`}
                strokeDashoffset={seg.dashOffset}
                className={cn(
                  "transition-[stroke-dashoffset,stroke-dasharray] duration-500",
                  seg.strokeClass
                )}
              />
            ))
          : null}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className={cn(
            "text-[24px] font-semibold tabular-nums leading-8 tracking-[0.41px]",
            textTone
          )}
        >
          {Math.round(clamped)}
        </span>
      </div>
    </div>
  );
};
